<template>
    <div :class="[outerClass, colSize, { 'form-field-disabled': isFormDisabled }]"
         :style="outerStyle">
        <label v-if="label"
               class="block"
               :class="{ 'required-label': hasRequiredAttr, [labelClass]: true }"
               :style="labelStyle">{{
                __(label)
            }}</label>
        <DatePicker autocomplete="off"
                    locale="pl"
                    :iconDisplay="iconDisplay"
                    :validation="validation"
                    :inputClass="['w-full', className]"
                    :inputStyle="inputStyle"
                    :class="[{ 'required-field': hasRequiredAttr }]"
                    :style="style"
                    :showIcon="showIcon"
                    :showButtonBar="showButtonBar"
                    :showTime="showTime"
                    :hourFormat="hourFormat"
                    :showOnFocus="showOnFocus"
                    :dateFormat="dateFormat.split(' ')[0]"
                    :manualInput="manualInput"
                    :minDate="minDate"
                    :maxDate="maxDate"
                    :disabled="isFormDisabled"
                    :modelValue="internalDate"
                    :selectionMode="selectionMode"
                    @date-select="dateSelect"
                    @clear-click="clearClick"
                    class="w-full rounded-md border px-3 py-2 text-sm"
                    @update:modelValue="handleDateChange"
                    @focus="focus"
                    v-focus="isFocus"
                    :unstyled="false"
                    :pt="{
                        root: {
                            name: fieldName,
                        },
                    }" />
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import DatePicker from 'primevue/datepicker';
import { __ } from '@pages/Helper/utils.js'

const emit = defineEmits(['update:field']);

const props = defineProps({
    fieldName: {
        type: String,
        required: true,
    },
    validation: {
        type: [Array, String],
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    size: {
        type: String,
        default: null
    },
    isFocus: {
        type: Boolean,
        default: false
    },
    isFormDisabled: {
        type: Boolean,
        default: false
    },
    showIcon: {
        type: Boolean,
        default: true
    },
    showButtonBar: {
        type: Boolean,
        default: true
    },
    showTime: {
        type: Boolean,
        default: false
    },
    hourFormat: {
        type: String,
        default: "24"
    },

    showOnFocus: {
        type: Boolean,
        default: true
    },
    dateFormat: {
        type: String,
        default: 'yy-mm-dd'
    },
    manualInput: {
        type: Boolean,
        default: true
    },
    labelClass: {
        type: String,
        default: null
    },
    labelStyle: {
        type: [Object, String],
        default: null
    },
    inRepeater: {
        type: Boolean,
        default: false
    },
    outerStyle: {
        type: [Object, String],
        default: null
    },
    style: {
        type: [Object, String],
        default: null
    },
    className: {
        type: String,
        default: null
    },
    inputStyle: {
        type: [Object],
        default: null
    },
    dateSelect: {
        type: Function,
        default: () => {
        }
    },
    clearClick: {
        type: Function,
        default: () => {
        }
    },
    onMounted: {
        type: Function,
        default: () => {
        }
    },
    formProps: {
        type: Object,
        default: null
    },
    minDate: {
        type: Date,
        default: null
    },
    maxDate: {
        type: Date,
        default: null
    },
    iconDisplay: {
        type: String,
        default: 'input'
    },
    selectionMode: {
        type: String,
        default: 'single' // lub 'range'
    },
    change: {
        type: Function,
        default: () => {
        }
    },
    focus: {
        type: Function,
        default: () => {
        }
    }
});

const field = defineModel('field');
const validation = ref(Array.isArray(props.validation) ? props.validation : [props.validation]);
const colSize = props?.size ? 'col-' + props.size : 'col';
const hasRequiredAttr = validation.value.includes('required');
const outerClass = ref('field');
const fieldStyle = ref(null);

// Wewnętrzna wartość Date dla DatePicker
const internalDate = ref(null);
const minDate = ref(props.minDate);
const maxDate = ref(props.maxDate);

if (props.inRepeater) {
    outerClass.value = null;
}

//-------------------------------------------------------------------------------------------------

// Konwersja field (string z backendu) na Date dla DatePicker
watch(() => field.value, (newVal) => {
    if (newVal == null || newVal === '') {
        internalDate.value = null;
        return;
    }

    // Jeśli już jest Date, użyj go, ALE sformatuj string wyjściowy, aby pozbyć się ISO "T...Z"
    if (newVal instanceof Date) {
        internalDate.value = newVal;

        // Wymuś formatowanie na string zgodny z wymogami (bez T i Z), jeśli jeszcze nie jest sformatowany
        // Robimy to tylko raz, aby nie wpaść w pętlę, sprawdzając czy to Date
        const formatted = formatDate(newVal);
        if (field.value !== formatted) {
            field.value = formatted;
            emit('update:field', formatted);
        }
        return;
    }

    // Parsuj string na Date
    internalDate.value = parseDate(newVal);
}, { immediate: true });

//-------------------------------------------------------------------------------------------------

onMounted(() => {
    if (props.onMounted) {
        props.onMounted(field.value)
    }
})

//-------------------------------------------------------------------------------------------------

const handleDateChange = (value) => {
    if (value == null || value === '') {
        internalDate.value = null;
        field.value = null;
        emit('update:field', null);
        if (props.change) props.change(null);
        return;
    }

    // Zapisz Date do wewnętrznej zmiennej
    internalDate.value = value;

    // Sformatuj i wyślij string do rodzica
    let formatted = null;

    // Sprawdź czy value jest typu Date
    if (value instanceof Date) {
        formatted = formatDate(value);
    } else if (value === null) {
        formatted = null;
    } else {
        // Jeśli z jakiegoś powodu to nie jest Date (np. manual input), spróbuj sparsować
        const parsed = parseDate(value);
        if (parsed) {
            formatted = formatDate(parsed);
        }
    }

    field.value = formatted;
    emit('update:field', formatted);
    if (props.change) props.change(formatted);
};

//-------------------------------------------------------------------------------------------------
// Funkcja parsująca string na Date
function parseDate(value) {
    if (!value || value === '') return null;
    if (value instanceof Date) return value;

    // Obsługa formatu "YYYY-MM-DD" lub "YYYY-MM-DD HH:mm:ss"
    if (typeof value === 'string') {
        // Zamień spację na "T" dla parsowania ISO
        const isoString = value.includes(' ') ? value.replace(' ', 'T') : value;
        const parsed = new Date(isoString);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
};

//-------------------------------------------------------------------------------------------------
// Funkcja formatująca Date na string "YYYY-MM-DD" lub "YYYY-MM-DD HH:mm"
function formatDate(date) {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    let result = `${year}-${month}-${day}`;

    if (props.showTime) {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        result += ` ${hours}:${minutes}`;
    }

    return result;
}
//-------------------------------------------------------------------------------------------------
</script>

<style lang="scss" scoped>
.form-field-disabled {

    // Nadajemy szare tło na główny kontener DatePickera (obejmujący ikonę i input)
    :deep([data-pc-name="datepicker"]) {
        background-color: #f5f6f8 !important;

        // Ustawiamy tło samego pola tekstowego na przezroczyste,
        // aby szare tło kontenera było widoczne na całej szerokości
        input {
            background-color: transparent !important;
        }
    }
}
</style>
